import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/apiClient";
import { Header, Footer } from "../components/Layout";
import { DossierFlash } from "../components/DossierFlash";
import { BetaTestersRegistry } from "../components/BetaTestersRegistry";
import { ArrowLeft, Download, Timer, MessageSquarePlus, Loader2, AlertCircle } from "lucide-react";

export default function DossierFlashPage() {
  const { dossierId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openFeedback, setOpenFeedback] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get(`/dossier-flash/${dossierId}`)
      .then((r) => { if (mounted) setData(r.data.data); })
      .catch((e) => { if (mounted) setError(e.response?.data?.detail || "Dossier introuvable."); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [dossierId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto" />
          <p className="text-sm text-slate-500 mt-3">Chargement du Dossier Flash…</p>
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
          <h1 className="font-heading font-black text-2xl mt-3">Dossier introuvable</h1>
          <p className="text-slate-600 mt-2">{error || "Ce dossier n'existe pas ou a expiré."}</p>
          <button onClick={() => navigate("/")} className="btn-emerald mt-6" data-testid="back-home-btn">
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const meta = data.meta || {};

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#1E293B] mb-4"><ArrowLeft className="w-4 h-4" /> Retour</Link>
        <div className="j-label mb-2">Dossier Flash · Instruction citoyenne</div>
        <h1 className="j-h1 font-heading" data-testid="dossier-title">Votre dossier structuré<br/><span className="text-[#10B981]">en 47 secondes.</span></h1>
        <p className="mt-3 text-slate-600 max-w-3xl">
          Extraction factuelle et vérification de complétude. Sources officielles Légifrance · Judilibre · Service-Public.fr (Licence Etalab 2.0).
          Aucun conseil juridique personnalisé (Loi 71-1130).
        </p>

        <div className="mt-6">
          <DossierFlash data={data} animated={true} />
        </div>

        {/* GAIN + CTA FEEDBACK */}
        <div className="mt-6 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-5 shadow-lg flex items-center gap-3 flex-wrap" data-testid="dossier-metrics">
          <Timer className="w-6 h-6 flex-shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <div className="text-xs uppercase tracking-widest font-bold opacity-90">Gain opérationnel</div>
            <div className="font-heading font-black text-lg">
              {((meta.time_saved_minutes || 240) / 60).toFixed(1)}h de tri manuel économisées
            </div>
          </div>
          <div className="text-xs opacity-90">Généré en {meta.seconds_generated || 47}s</div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <button onClick={() => window.print()} className="btn-emerald min-h-[48px]" data-testid="dossier-export-btn">
            <Download className="w-5 h-5" /> Exporter le Rapport (PDF)
          </button>
          <button
            onClick={() => setOpenFeedback(true)}
            className="min-h-[48px] px-5 py-2.5 rounded-xl bg-white border-2 border-emerald-500 text-emerald-700 font-heading font-black text-sm hover:bg-emerald-50 transition-colors inline-flex items-center gap-2 shadow-sm"
            data-testid="dossier-feedback-cta-top"
          >
            <MessageSquarePlus className="w-4 h-4" /> Votre diagnostic est utile ? Laisser votre avis au registre
          </button>
        </div>

        {/* CTA ancre en bas de la fiche */}
        <div className="mt-10 pt-6 border-t border-slate-200 text-center" data-testid="dossier-feedback-anchor">
          <p className="text-sm text-slate-600 mb-3">Ce diagnostic vous a-t-il permis de mieux comprendre votre dossier ?</p>
          <button
            onClick={() => setOpenFeedback(true)}
            className="min-h-[48px] px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-heading font-black text-sm transition-colors inline-flex items-center gap-2 shadow-md"
            data-testid="dossier-feedback-cta"
          >
            <MessageSquarePlus className="w-4 h-4" /> Votre diagnostic est utile ? Laisser votre avis au registre
          </button>
        </div>
      </div>

      <BetaTestersRegistry autoOpen={openFeedback} onAutoOpenHandled={() => setOpenFeedback(false)} />
      <Footer />
    </div>
  );
}
